import { colors, __SWEEP_DESIGN_TYPOGRAPHY_STYLES___, Typography } from '@sweep-io/sweep-design';
import { dataTableVariants } from './dataTableVariants';
import { DataTableVariant } from './TableTypes';
import type { ReactNode } from 'react';
import Stack from '@mui/material/Stack';

export const ACTION_BUTTONS_KEY = '__actions';
export const SELECTION_COLUMN_FIELD = '__selection';
export const DRAGGABLE_TABLE_OVERLAY_COLOR = 'rgba(236,242,245,0.7)';

export const linearGradient = (color: string) =>
  `linear-gradient(to right, rgba(255, 255, 255, 0.1) -4%, ${color} 10%) `;

export enum DataTableId {
  TerritoriesTable = 'territories-table',
  AssignmentRulesTable = 'assignment-rules-table',
  TieBreakersTable = 'tie-breakers-table',
  PrioritizePicklistTable = 'prioritize-picklist-table',
  MasterRecordRulesTable = 'master-record-rules-table',
  RollupsTable = 'rollups-table',
  AutomationsTable = 'automations-table',
  AiAssistantPage = 'ai-assistant-page',

  //assignments
  AssignmentGroupsTable = 'assignment-groups-table',
  AssignmentGroupMembersTable = 'assignment-group-members-table',
  AssignmentsMembersTable = 'assignments-members-table',

  //documentation
  ObjectCardTable = 'object-card-table',
  DocumentationRelatedListTable = 'documentation-related-list-table',
  DocumentationValidationRulesTable = 'documentation-validation-rules-table',
  DocumentationWorkflowRulesTable = 'documentation-workflow-rules-table',
  DocumentationFlowsTable = 'documentation-flows-table',
  DocumentationProcessBuilderFlowsTable = 'documentation-process-builder-flows-table',
  DocumentationApprovalProcessesTable = 'documentation-approval-processes-table',
  DocumentationApexTriggersTable = 'documentation-apex-triggers-table',
  DocumentationApexClassesTable = 'documentation-apex-classes-table',
  DocumentationReportsTable = 'documentation-reports-table',
  DocumentationLayoutsTable = 'documentation-layouts-table',
  DocumentationFieldsTable = 'documentation-fields-table',
  DocumentationObjectsTable = 'documentation-objects-table',
  DocumentationEmailTemplatesTable = 'documentation-email-templates-table',
  DocumentationSweepAutomationsTable = 'documentation-sweep-automations-table',
  DocumentationSweepAlertsTable = 'documentation-sweep-alerts-table',
  DocumentationSweepDedupeTable = 'documentation-sweep-dedupe-table',
  DocumentationSweepMatchingTable = 'documentation-sweep-matching-table',
  DocumentationSweepAssignmentTable = 'documentation-sweep-assignment-table',
  DocumentationSweepScheduledAssignmentTable = 'documentation-sweep-scheduled-assignment-table',
  DocumentationCpqDataTable = 'documentation-cpq-data-table',
  DocumentationRecordTypesTable = 'documentation-record-types-table',
  DocumentationPathsTable = 'documentation-paths-table',
  DocumentationRelatedObjectsTable = 'documentation-related-objects-table',
  DocumentationStandardFieldsTable = 'documentation-standard-fields-table',
  DocumentationCustomFieldsTable = 'documentation-custom-fields-table',
  DocumentationRollupsTable = 'documentation-rollups-table',
  DocumentationTagsCenterTable = 'documentation-tags-center-table',

  //documentation-expanded
  DocumentationExpandedFieldsTable = 'documentation-expanded-fields-table',
  DocumentationExpandedRecordTypesTable = 'documentation-expanded-record-types-table',
  DocumentationExpandedAutomationsTable = 'documentation-expanded-automations-table',
  DocumentationExpandedApexTable = 'documentation-expanded-apex-table',
  DocumentationExpandedValidationRulesTable = 'documentation-expanded-validation-rules-table',
  DocumentationExpandedSweepAutomationsTable = 'documentation-expanded-sweep-automations-table',
  DocumentationExpandedAlertsTable = 'documentation-expanded-alerts-table',
  DocumentationExpandedDedupeTable = 'documentation-expanded-dedupe-table',
  DocumentationExpandedAssignmentsTable = 'documentation-expanded-assignments-table',
  DocumentationExpandedScheduledAssignmentsTable = 'documentation-expanded-scheduled-assignments-table',
  DocumentationExpandedCpqDataTable = 'documentation-expanded-cpq-data-table',
  DocumentationExpandedLayoutsTable = 'documentation-expanded-layouts-table',
  DocumentationExpandedRelatedObjectsTable = 'documentation-expanded-related-objects-table',
  DocumentationExpandedCardsListTable = 'documentation-expanded-cards-list-table',
  DocumentationExpandedOtherTable = 'documentation-expanded-other-table',

  //snowflake documentation
  SnowflakeDocTable = 'snowflake-doc-table',
  SnowflakeDocView = 'snowflake-doc-view',
  SnowflakeDocTask = 'snowflake-doc-task',
  SnowflakeDocStream = 'snowflake-doc-stream',
  SnowflakeDocProcedure = 'snowflake-doc-procedure',
  SnowflakeDocFunction = 'snowflake-doc-function',
  SnowflakeDocStage = 'snowflake-doc-stage',
  SnowflakeDocSequence = 'snowflake-doc-sequence',

  //data360 documentation
  Data360DocDataModelObject = 'data360-doc-data-model-object',
  Data360DocDataModelField = 'data360-doc-data-model-field',
  Data360DocDataLakeObject = 'data360-doc-data-lake-object',
  Data360DocDataLakeField = 'data360-doc-data-lake-field',
  Data360DocDataGraph = 'data360-doc-data-graph',
  Data360DocDataSource = 'data360-doc-data-source',
  Data360DocDataSourceObject = 'data360-doc-data-source-object',
  Data360DocDataSourceTenant = 'data360-doc-data-source-tenant',
  Data360DocDataConnector = 'data360-doc-data-connector',
  Data360DocExternalDataConnector = 'data360-doc-external-data-connector',
  Data360DocExternalDataSource = 'data360-doc-external-data-source',
  Data360DocDataStreamDefinition = 'data360-doc-data-stream-definition',
  Data360DocDataStreamTemplate = 'data360-doc-data-stream-template',
  Data360DocDataTransform = 'data360-doc-data-transform',
  Data360DocDataSourceBundleDefinition = 'data360-doc-data-source-bundle-definition',
  Data360DocDataSrcDataModelFieldMap = 'data360-doc-data-src-data-model-field-map',
  Data360DocFieldMapping = 'data360-doc-field-mapping',
  Data360DocObjectMapping = 'data360-doc-object-mapping',
  Data360DocCalculatedInsight = 'data360-doc-calculated-insight',
  Data360DocInsightDimension = 'data360-doc-insight-dimension',
  Data360DocInsightMeasure = 'data360-doc-insight-measure',
  Data360DocSegment = 'data360-doc-segment',
  Data360DocMarketSegmentActivation = 'data360-doc-market-segment-activation',
  Data360DocActivation = 'data360-doc-activation',
  Data360DocActivationTarget = 'data360-doc-activation-target',
  Data360DocActivationExternalPlatform = 'data360-doc-activation-external-platform',
  Data360DocActivationPlatform = 'data360-doc-activation-platform',
  Data360DocDataAction = 'data360-doc-data-action',
  Data360DocDataActionTarget = 'data360-doc-data-action-target',
  Data360DocDataSpace = 'data360-doc-data-space',
  Data360DocIdentityResolutionRuleset = 'data360-doc-identity-resolution-ruleset',
  Data360DocIdentityResolutionRule = 'data360-doc-identity-resolution-rule',
  Data360DocCleanRoomCollaboration = 'data360-doc-clean-room-collaboration',
  Data360DocCleanRoomProvider = 'data360-doc-clean-room-provider',
  Data360DocCleanRoomTemplate = 'data360-doc-clean-room-template',
  Data360DocDataShare = 'data360-doc-data-share',
  Data360DocDataShareTarget = 'data360-doc-data-share-target',
  Data360DocDataPackageKitDefinition = 'data360-doc-data-package-kit-definition',
  Data360DocDataPackageKitObject = 'data360-doc-data-package-kit-object',
  Data360DocSearchIndex = 'data360-doc-search-index',
  Data360DocSemanticLayer = 'data360-doc-semantic-layer',

  //homepage
  PotentialIssuesTable = 'potential-issues-table',

  //automations
  AutomationOrderDialog = 'automation-order-dialog',
  MarketingAttributionWeightsTable = 'marketing-attribution-weights-table',
  AutomationVersionHistoryDialog = 'automation-version-history-dialog',
  AuditTable = 'audit-table',
  AutomationScheduledAssignmentTable = 'automation-scheduled-assignment-table',
  AutomationAssignmentRulesTable = 'automation-assignment-rules-table',
  AutomationScheduledJobsTable = 'automation-scheduled-jobs-table',
  AutomationAlertsTable = 'automation-alerts-table',
  AutomationConditionalActionsTable = 'automation-conditional-actions-table',
  AutomationSlackFields = 'automation-slack-fields',
  TimeOffHistoryDialog = 'time-off-history-dialog',
  AutomationDedupMatchingTable = 'automation-dedup-matching-table',
  AutomationMarketingAttributionModelsTable = 'automation-marketing-attribution-models-table',
  AutomationScheduledReportsTable = 'automation-scheduled-reports-table',

  //funnel-map-flow
  FunnelVersionsTable = 'funnel-versions-table',
  TemplateFieldsTable = 'template-fields-table',
  PluginFieldsTable = 'plugin-fields-table',
  BaseCriteriaTable = 'base-criteria-table',
  PicklistTable = 'picklist-table',
  FieldsManagementTableSingleObject = 'fields-management-table-single-object',

  //org-migration
  OrgMigrationObjects = 'org-migration-objects-table',
  OrgMigrationPermissions = 'org-migration-permissions-table',
  OrgMigrationAutomations = 'org-migration-automations-table',
  OrgMigrationUserStoriesTable = 'org-migration-user-stories-table',

  //demo
  NewTableDesignDemo = 'new-table-design-demo',
  SortableDataTableDemo = 'sortable-data-table-demo',
  DraggableDataTableDemo = 'draggable-data-table-demo',
  ExpandableDataTableDemo = 'expandable-data-table-demo',

  //devops-center
  DeploymentReportTable = 'deployment-report-table',
  DevopsCenterPageTable = 'devops-center-page-table',

  //tests
  DataTableSpec = 'data-table-spec',

  //hubspot
  HubspotStatusesTable = 'hubspot-statuses-table',
  HubspotCampaignsTable = 'hubspot-campaigns-table',

  //change-feed
  SalesforceChangeFeedTable = 'salesforce-change-feed-table',
  SalesforceLoginHistoryTable = 'salesforce-login-history-table',

  //agents
  IssuesTable = 'issues-table',

  //simulation
  SimulationAssignmentsPerUserTable = 'simulation-assignments-per-user-table',
  SimulationAssignmentsLogTable = 'simulation-assignments-log-table',

  //environments
  EnvironmentsPageTable = 'environments-page-table',
}

export const getTableDividerCss = (isOver: boolean, isDragging: boolean) => ({
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: '24px',
    backgroundColor: isOver && !isDragging ? 'transparent' : colors.grey[120],
  },
});

export const stickyHeaderStyles = {
  zIndex: 3,
  position: 'sticky',
  top: '0px',
};

export const cellChevronStyles = {
  '.cell-chevron, [data-column-id="__chevron"]': {
    borderRight: 'none',
    padding: 0,
    paddingLeft: 1,
    opacity: 0,
    transition: 'opacity 0.15s ease-in-out',

    '&:after': {
      display: 'none',
    },
  },

  '[data-column-id="__chevron"]': {
    opacity: 1,
  },

  '[data-column-id="__chevron"] + th, .cell-chevron + td': {
    pl: 0,
  },

  '.cell-chevron > div': {
    width: '16px',
  },

  'tr:hover, tr[data-expanded="true"]': {
    '.cell-chevron, [data-column-id="__chevron"]': {
      opacity: 1,
    },
    '.cell-chevron + td, [data-column-id="__chevron"] + td': {
      textDecoration: 'underline',
    },
  },

  'tr[data-expanded="true"]': {
    '.cell-chevron ~ td .MuiTypography-root, .cell-chevron ~ td ': {
      fontWeight: 600,
    },
  },
};

export const TableTypography = ({
  children,
  variant = DataTableVariant.medium,
  textColor,
}: {
  children: string | ReactNode;
  variant?: DataTableVariant;
  textColor?: string;
}) => {
  return (
    <>
      {typeof children === 'string' ? (
        <Typography
          variant={dataTableVariants[variant].fontVariant}
          color={textColor ?? dataTableVariants[variant].fontColor}
          whiteSpace="break-spaces"
          wordBreak="break-all"
        >
          {children}
        </Typography>
      ) : (
        <Stack
          sx={{
            fontSize:
              __SWEEP_DESIGN_TYPOGRAPHY_STYLES___?.[dataTableVariants[variant].fontVariant]
                ?.fontSize,
            fontWeight:
              __SWEEP_DESIGN_TYPOGRAPHY_STYLES___?.[dataTableVariants[variant].fontVariant]
                ?.fontWeight,
            lineHeight:
              __SWEEP_DESIGN_TYPOGRAPHY_STYLES___?.[dataTableVariants[variant].fontVariant]
                ?.lineHeight,
            whiteSpace: 'break-spaces',
            wordBreak: 'break-word',
          }}
        >
          {children}
        </Stack>
      )}
    </>
  );
};
